import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef, createContext, useContext } from "react";
import api from "./utils/api";
import { BranchProvider } from "./contexts/BranchContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import { BarcodeScannerProvider } from "./contexts/BarcodeScannerContext";
import SubscriptionGuard from "./components/SubscriptionGuard";
import "@/App.css";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Customers from "./pages/Customers";
import Batches from "./pages/Batches";
import BatchSchedule from "./pages/BatchSchedule";
import BatchBedSchedule from "./pages/BatchBedSchedule";
import CheckIn from "./pages/CheckIn";
import Analytics from "./pages/Analytics";
import AttendanceReport from "./pages/AttendanceReport";
import Notifications from "./pages/Notifications";
import NotificationsAbsent from "./pages/NotificationsAbsent";
import WaitingList from "./pages/WaitingList";
import Users from "./pages/Users";
import BranchManagement from "./pages/BranchManagement";
import StockItems from "./pages/StockItems";
import StockManagement from "./pages/StockManagement";
import CompanyLedger from "./pages/CompanyLedger";
import SalesRecords from "./pages/SalesRecords";
import PettyCashManagement from "./pages/PettyCashManagement";
import AuthCallback from "./pages/AuthCallback";
import Tracking from "./pages/Tracking";
import StaffLoginLogout from "./pages/StaffLoginLogout";
import PremiumSchedule from "./pages/PremiumSchedule";
import DailyCheckIn from "./pages/DailyCheckIn";
import DeletedCustomers from "./pages/DeletedCustomers";
import BMI from "./pages/BMI";
import AIAssistant from "./pages/AIAssistant";
import AISettings from "./pages/AISettings";
import BranchBillingSettings from "./pages/BranchBillingSettings";
import BranchSettings from "./pages/BranchSettings";
import SubscriptionPage from "./pages/SubscriptionPage";
import Counselling from "./pages/Counselling";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    if (location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await api.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [location]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async (reason) => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
    localStorage.removeItem('token');
    if (reason) sessionStorage.setItem('logout_reason', reason);
    setUser(null);
  };

  // Security: auto-logout the moment the app tab is hidden (new tab, minimize, window covered)
  // Exempt: Absent Calls (phone dialer) and Counselling (phone follow-ups / report photos) pages
  const AUTO_LOGOUT_EXEMPT_PATHS = ['/notifications-absent', '/counselling'];
  useEffect(() => {
    if (!user) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') return;
      if (AUTO_LOGOUT_EXEMPT_PATHS.some(p => window.location.pathname.startsWith(p))) return;
      logout('auto');
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F8]">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#2C4C3B] border-r-transparent"></div>
          <p className="mt-4 text-[#2C4C3B]">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

function AppRouter() {
  const location = useLocation();
  const { user } = useAuth();

  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <BranchProvider user={user}>
      <SubscriptionProvider>
        <BarcodeScannerProvider>
          <Routes>
          <Route path="/login" element={<Login />} />
          {/* Subscription page should NOT be wrapped in SubscriptionGuard */}
          <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
          
          {/* All other routes require BOTH auth AND subscription */}
          <Route path="/dashboard" element={<ProtectedRoute><SubscriptionGuard><Dashboard /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute><SubscriptionGuard><Customers /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/batches" element={<ProtectedRoute><SubscriptionGuard><Batches /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/batch-schedule" element={<ProtectedRoute><SubscriptionGuard><BatchSchedule /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/batch-bed-schedule" element={<ProtectedRoute><SubscriptionGuard><BatchBedSchedule /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/premium-schedule" element={<ProtectedRoute><SubscriptionGuard><PremiumSchedule /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/check-in" element={<ProtectedRoute><SubscriptionGuard><CheckIn /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/waiting-list" element={<ProtectedRoute><SubscriptionGuard><WaitingList /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/bmi" element={<ProtectedRoute><SubscriptionGuard><BMI /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/attendance-report" element={<ProtectedRoute><SubscriptionGuard><AttendanceReport /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><SubscriptionGuard><Notifications /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/notifications-absent" element={<ProtectedRoute><SubscriptionGuard><NotificationsAbsent /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/counselling" element={<ProtectedRoute><SubscriptionGuard><Counselling /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/analytics" element={<ProtectedRoute><SubscriptionGuard><Analytics /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/tracking" element={<ProtectedRoute><SubscriptionGuard><Tracking /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/daily-check-in" element={<ProtectedRoute><SubscriptionGuard><DailyCheckIn /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/deleted" element={<ProtectedRoute><SubscriptionGuard><DeletedCustomers /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/staff-login-logout" element={<ProtectedRoute><SubscriptionGuard><StaffLoginLogout /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/stock-items" element={<ProtectedRoute><SubscriptionGuard><StockItems /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/stock-management" element={<ProtectedRoute><SubscriptionGuard><StockManagement /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/company-ledger" element={<ProtectedRoute><SubscriptionGuard><CompanyLedger /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/sales-records" element={<ProtectedRoute><SubscriptionGuard><SalesRecords /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/petty-cash" element={<ProtectedRoute><SubscriptionGuard><PettyCashManagement /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/branch-management" element={<ProtectedRoute><SubscriptionGuard><BranchManagement /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/branch-settings" element={<ProtectedRoute><SubscriptionGuard><BranchSettings /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/branches/:branchId/billing" element={<ProtectedRoute><SubscriptionGuard><BranchBillingSettings /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/branches/billing" element={<ProtectedRoute><SubscriptionGuard><BranchBillingSettings /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/users" element={<ProtectedRoute><SubscriptionGuard><Users /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/ai-assistant" element={<ProtectedRoute><SubscriptionGuard><AIAssistant /></SubscriptionGuard></ProtectedRoute>} />
          <Route path="/ai-settings" element={<ProtectedRoute><SubscriptionGuard><AISettings /></SubscriptionGuard></ProtectedRoute>} />
          
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        </BarcodeScannerProvider>
      </SubscriptionProvider>
    </BranchProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
